import { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import {
  FaPhone, FaCheck, FaArrowRight, FaChevronLeft, FaChevronRight,
  FaCalculator, FaChartLine, FaLightbulb, FaLaptop,
  FaBook, FaClipboardCheck, FaFileAlt, FaUsers,
  FaWarehouse, FaHandshake, FaCog, FaBriefcase,
  FaChartBar, FaShieldAlt, FaAward
} from "react-icons/fa";
import { Header, Footer, TroubleSection, PageHero } from "./components/SharedComponents";
import BookkeepingPage from "./pages/BookkeepingPage";
import PricingPage from "./pages/PricingPage";
import GenericServicePage from "./pages/GenericServicePage";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_1280a4a7-60db-4fa6-9a32-77c71d127606/artifacts/9wayhrd3_United%20Taxpert%20Logo.png";
const AFSP_LOGO = "https://customer-assets.emergentagent.com/job_website-ready-go/artifacts/1eieoce2_Logo%20-%20AFSP-Preparer.png";
const AFSP_CERT_PDF = "https://customer-assets.emergentagent.com/job_website-ready-go/artifacts/g358jhj8_FY2026%20IRS%20AFSP%20Certificate.pdf";
const IRS_EFILE_LOGO = "https://cdn.worldvectorlogo.com/logos/authorized-irs-e-file-provider-logo.svg";

// ---- HERO SECTION ----
const heroSlides = [
  { title: "Smart Accounting + Savvy Advice = Sound Business", subtitle: "Welcome to United Taxpert, where expert financial solutions and precision meet. Trust us to navigate your finances with accuracy, reliability, and a commitment to your growth.", bg: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1600&auto=format&fit=crop&q=80" },
  { title: "Expert Tax Planning for Every Business Size", subtitle: "Comprehensive tax solutions tailored to your unique business needs. Let our 25+ years of expertise work for you and help you maximize your returns.", bg: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1600&auto=format&fit=crop&q=80" },
  { title: "Precision Financial Management You Can Trust", subtitle: "From bookkeeping to compliance audits, we deliver accurate, timely, and insightful financial management that empowers your business decisions.", bg: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=1600&auto=format&fit=crop&q=80" }
];

const HeroSection = () => {
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setCurrent(prev => (prev + 1) % heroSlides.length), 5000);
    return () => clearInterval(timer);
  }, []);
  return (
    <section className="hero-section" style={{ backgroundImage: `url(${heroSlides[current].bg})` }}>
      <div className="hero-overlay" />
      <div className="container hero-content">
        <div className="hero-text">
          <h1>{heroSlides[current].title}</h1>
          <p>{heroSlides[current].subtitle}</p>
          <a href="mailto:refunds@unitedtaxpert.com" className="btn btn-outline-white">Contact Us</a>
        </div>
        <div className="hero-badges-container">
          <div className="hero-logo-animated">
            <img src={LOGO_URL} alt="United Taxpert" className="hero-logo-img" />
          </div>
          <div className="hero-badge">
            <div className="badge-inner">
              <div className="badge-star">&#9733;</div>
              <div className="badge-main">Fastest Returns</div>
              <div className="badge-sub">Maximum Refunds Guaranteed</div>
            </div>
          </div>
        </div>
      </div>
      <button className="hero-arrow left" onClick={() => setCurrent((current - 1 + heroSlides.length) % heroSlides.length)}><FaChevronLeft /></button>
      <button className="hero-arrow right" onClick={() => setCurrent((current + 1) % heroSlides.length)}><FaChevronRight /></button>
      <div className="hero-dots">
        {heroSlides.map((_, i) => <button key={i} className={`hero-dot ${i === current ? "active" : ""}`} onClick={() => setCurrent(i)} />)}
      </div>
    </section>
  );
};

// ---- SERVICE HIGHLIGHTS ----
const ServiceHighlights = () => (
  <section className="service-highlights">
    <div className="container">
      <div className="highlights-grid">
        {[{ icon: <FaChartLine size={36} />, title: "Financial Mastery", desc: "Helping you take full control of your finances with strategic tax planning and smart financial management.", link: "/services/fractional-cfo" },
          { icon: <FaCalculator size={36} />, title: "Precise Accounting", desc: "Accurate bookkeeping and meticulous tax records to ensure compliance and peace of mind during every financial season.", link: "/services/bookkeeping" },
          { icon: <FaLightbulb size={36} />, title: "Savvy Advisory", desc: "Expert guidance on tax optimization, business structure, and financial growth – tailored to your unique goals.", link: "/services/tax-planning" },
          { icon: <FaLaptop size={36} />, title: "Tech Edge", desc: "Integrating advanced tax software and digital tools to simplify processes, improve accuracy, and save you valuable time.", link: "/services/payroll-services" }
        ].map((s, i) => (
          <div key={i} className="highlight-card">
            <div className="highlight-icon">{s.icon}</div>
            <h3>{s.title}</h3>
            <p>{s.desc}</p>
            <Link to={s.link} className="read-more">Read More <FaArrowRight /></Link>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---- STAND OUT SECTION ----
const coreStrengths = [
  "Strategic Value Proposition that drives measurable results",
  "Tailored Product & Service Positioning for every business type",
  "Accurate Financial Forecasting to support informed decisions",
  "Efficient Data Management and secure record handling",
  "In-depth Organizational Analysis to improve operations",
  "Business Feasibility Studies ensuring sustainable growth"
];

const StandOutSection = () => (
  <section id="about" className="standout-section">
    <div className="container">
      <div className="standout-grid">
        <div className="standout-images">
          <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&auto=format&fit=crop&q=80" alt="Team working" className="standout-img-main" />
          <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&auto=format&fit=crop&q=80" alt="Meeting" className="standout-img-secondary" />
          <div className="standout-badge"><span className="badge-num">12+</span><span className="badge-label">Legacy</span><span className="badge-label2">Expertise</span></div>
        </div>
        <div className="standout-content">
          <p className="section-label">Why United Taxpert</p>
          <h2>What Makes <span className="text-blue">United Taxpert</span> Stand Out</h2>
          <p className="standout-desc">With over 25 years of trusted expertise, United Taxpert brings together financial intelligence, modern technology, and strategic insight to deliver exceptional tax and business consulting solutions.</p>
          <p className="core-label">Our Core Strengths</p>
          <ul className="core-list">
            {coreStrengths.map((s, i) => <li key={i}><FaCheck className="check-icon" /> {s}</li>)}
          </ul>
          <div className="founder-row">
            <img src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=80&h=80&auto=format&fit=crop&q=80" alt="Vic Agarwal" className="founder-img" />
            <div className="founder-info"><span className="founder-name">Vic Agarwal</span><span className="founder-title">Founder Partner</span></div>
            <div className="founder-sig">Vic Agarwal</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ---- QUOTE SECTION ----
const QuoteSection = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", employees: "", service: "", plan: "", revenue: "", description: "" });
  const [submitted, setSubmitted] = useState(false);
  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = e => { 
    e.preventDefault(); 
    // Compose email body with form data
    const subject = encodeURIComponent("Free Quote Request");
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone}\nNo. of Employees: ${form.employees}\nService Request: ${form.service}\nService Plan: ${form.plan}\nAnnual Revenue: ${form.revenue}\n\nDescription:\n${form.description}`
    );
    // Open mailto link
    window.location.href = `mailto:info@unitedtaxassociates.com?subject=${subject}&body=${body}`;
    setSubmitted(true); 
    setTimeout(() => setSubmitted(false), 4000); 
  };
  return (
    <section id="contact" className="quote-section">
      <div className="quote-overlay" />
      <div className="container">
        <div className="quote-grid">
          <div className="quote-left">
            <h2>Get a Free Quote Calculation</h2>
            <p>We will reply as soon as possible!</p>
          </div>
          <div className="quote-form-wrap">
            {submitted ? (
              <div className="form-success"><FaCheck /> Thank you! We'll be in touch soon.</div>
            ) : (
              <form className="quote-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <input type="text" name="name" placeholder="Your Name" value={form.name} onChange={handleChange} required />
                  <input type="email" name="email" placeholder="Email" value={form.email} onChange={handleChange} required />
                </div>
                <div className="form-row">
                  <input type="tel" name="phone" placeholder="Contact Number" value={form.phone} onChange={handleChange} />
                  <input type="number" name="employees" placeholder="No. of Employees" value={form.employees} onChange={handleChange} />
                </div>
                <div className="form-row">
                  <select name="service" value={form.service} onChange={handleChange}>
                    <option value="">Service Request</option>
                    {["Bookkeeping","Tax Planning","Payroll Services","Compliance Audit","Financial Statements","Business Acquisitions","Fractional CFO Services","Administrative Services"].map(o => <option key={o}>{o}</option>)}
                  </select>
                  <select name="plan" value={form.plan} onChange={handleChange}>
                    <option value="">Choose Service Plan</option>
                    {["Starter","Growth","Enterprise"].map(o => <option key={o}>{o}</option>)}
                  </select>
                  <select name="revenue" value={form.revenue} onChange={handleChange}>
                    <option value="">Annual Revenue</option>
                    {["Under $100K","$100K - $500K","$500K - $1M","$1M - $5M","Over $5M"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <textarea name="description" placeholder="Write a short description" value={form.description} onChange={handleChange} rows={4} />
                <button type="submit" className="btn btn-primary btn-full">Get a Free Quote</button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// ---- SERVICES SECTION ----
const services = [
  { icon: <FaBook size={32} />, title: "Bookkeeping", path: "/services/bookkeeping", desc: "Accurate and organized financial records to keep your business running smoothly and ready for every financial decision.", blue: true },
  { icon: <FaCalculator size={32} />, title: "Tax Planning", path: "/services/tax-planning", desc: "Strategic tax-saving plans tailored to minimize liabilities and maximize profitability while staying compliant.", blue: false },
  { icon: <FaUsers size={32} />, title: "Payroll Services", path: "/services/payroll-services", desc: "Seamless payroll management that ensures accuracy, timely payments, and compliance with all legal obligations.", blue: true },
  { icon: <FaWarehouse size={32} />, title: "Inventory Management", path: "/services/inventory-management", desc: "Efficient tracking and reporting systems that help optimize stock levels and enhance financial accuracy.", blue: false },
  { icon: <FaClipboardCheck size={32} />, title: "Financial Reporting & Audit", path: "/services/financial-reporting-audit", desc: "GAAP-compliant financial statements, compliance audits, and audit-ready reporting that builds trust with investors, lenders, and regulators.", blue: false },
  { icon: <FaFileAlt size={32} />, title: "Business Acquisitions", path: "/services/business-acquisitions", desc: "Comprehensive financial due diligence and advisory for smooth, secure, and strategic business acquisitions.", blue: true },
  { icon: <FaHandshake size={32} />, title: "Administrative Services", path: "/services/administrative-services", desc: "End-to-end administrative support to streamline operations and free your team for high-value business priorities.", blue: false },
  { icon: <FaBriefcase size={32} />, title: "Fractional CFO Services", path: "/services/fractional-cfo", desc: "Executive-level financial strategy, cash flow management, and investor relations — delivered on a flexible, cost-effective fractional basis.", blue: true }
];

const ServicesSection = () => (
  <section id="services" className="services-section">
    <div className="container">
      <p className="section-label">Our Services</p>
      <div className="services-header">
        <div><h2><em className="text-blue">Expert</em> Accounting, <br />Exceptional Results</h2></div>
        <p className="services-intro">At United Taxpert, we deliver precise, reliable, and growth-focused financial solutions. From compliance audits to business acquisitions, our services ensure your company operates efficiently and stays fully ready.</p>
      </div>
      <div className="services-grid">
        {services.map((s, i) => (
          <div key={i} className={`service-card ${s.blue ? "blue" : "white"}`}>
            <div className="service-icon">{s.icon}</div>
            <h3>{s.title}</h3>
            <p>{s.desc}</p>
            <Link to={s.path} className="read-more">Read More <FaArrowRight /></Link>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---- PRIORITY / SKILLS SECTION ----
const specializedServices = [
  { icon: <FaBriefcase size={28} />, title: "Fractional CFO Services", desc: "Strategic financial leadership without the full-time cost. Our fractional CFOs deliver executive-level insight.", blue: true, path: "/services/fractional-cfo" },
  { icon: <FaChartBar size={28} />, title: "Business Feasibility", desc: "Comprehensive feasibility analysis to evaluate viability, risks, and opportunities for your business ventures.", blue: false, path: "/services/tax-planning" },
  { icon: <FaHandshake size={28} />, title: "Mergers & Acquisitions", desc: "Expert guidance through every stage of M&A transactions for successful business transitions.", blue: false, path: "/services/business-acquisitions" },
  { icon: <FaChartLine size={28} />, title: "Bookkeeping and Performance Analytics", desc: "Advanced analytics to measure, monitor, and improve your business financial performance.", blue: true, path: "/services/bookkeeping" }
];
const skills = [{ name: "Expertise", pct: 95 }, { name: "Knowledge", pct: 92 }, { name: "Reputation", pct: 98 }];

const PrioritySection = () => (
  <section className="priority-section">
    <div className="container">
      <div className="priority-top">
        <div className="specialized-grid">
          {specializedServices.map((s, i) => (
            <div key={i} className={`service-card ${s.blue ? "blue" : "white"}`}>
              <div className="service-icon">{s.icon}</div>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
              <Link to={s.path} className="read-more">Read More <FaArrowRight /></Link>
            </div>
          ))}
        </div>
        <div className="priority-right">
          <p className="section-label">Our Priority</p>
          <h2>We Provide <span className="text-blue">*Services</span> to All Business</h2>
          <p className="priority-desc">A summary of the types of clients served by our firm, including industries or categories of businesses, and any specialized areas of focus.</p>
          <div className="skills-wrap">
            <p className="skills-heading">Our Skill:</p>
            {skills.map((sk, i) => (
              <div key={i} className="skill-item">
                <div className="skill-label"><span>{sk.name}</span><span>{sk.pct}%</span></div>
                <div className="skill-bar"><div className="skill-fill" style={{ width: `${sk.pct}%` }} /></div>
              </div>
            ))}
          </div>
          <div className="accreditations home-acc">
            <p className="acc-label">Affiliations:</p>
            <div className="acc-logos-real">
              <div className="natp-badge"><div className="natp-logo-text">NATP</div><div className="natp-sub">National Association of Tax Professionals</div><div className="natp-prov">Member</div></div>
              <div className="bbb-badge"><div className="bbb-logo-text">BBB</div><div className="bbb-sub">Accredited Business</div><div className="bbb-stars">★★★★★</div></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ---- ABOUT PAGE ----
const AboutHero = () => (
  <section className="page-hero">
    <div className="page-hero-overlay" />
    <div className="container"><div className="page-hero-content">
      <h1>About Us</h1>
      <div className="breadcrumb"><Link to="/">Home</Link><span>/</span><span>About Us</span></div>
    </div></div>
  </section>
);

const aboutStrengths = [
  { label: "Value Proposition" }, { label: "Data Management" },
  { label: "Product Positioning" }, { label: "Organizational Analysis" },
  { label: "Financial Forecasting" }, { label: "Business Feasibility" }
];

const AboutStandOut = () => (
  <section className="about-standout">
    <div className="container">
      <div className="about-standout-grid">
        <div className="about-images">
          <div className="about-img-collage">
            <img src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=600&auto=format&fit=crop&q=80&grayscale" alt="Business team" className="about-img-main" />
            <img src="https://images.unsplash.com/photo-1556761175-4b46a572b786?w=400&auto=format&fit=crop&q=80&grayscale" alt="Finance analysis" className="about-img-secondary" />
          </div>
          <div className="about-expertise-badge"><span className="badge-num">25+</span><span className="badge-label">Legacy</span><span className="badge-label2">Expertise</span></div>
        </div>
        <div className="about-content">
          <p className="section-label">Why United Taxpert</p>
          <h2>What makes <span className="text-blue">United Taxpert</span> standout</h2>
          <hr className="about-divider" />
          <div className="about-strengths-grid">
            {aboutStrengths.map((s, i) => (
              <div key={i} className="about-strength-item"><FaCheck className="about-check" /><span>{s.label}</span></div>
            ))}
          </div>
          <div className="founder-row">
            <img src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=80&h=80&auto=format&fit=crop&q=80" alt="Vic Agarwal" className="founder-img" />
            <div className="founder-info"><span className="founder-name">Vic Agarwal</span><span className="founder-title">Founder Partner</span></div>
            <div className="founder-sig">Vic Agarwal</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const MissionSection = () => (
  <section className="mission-section">
    <div className="mission-overlay" />
    <div className="container mission-content">
      <div className="mission-text">
        <h2>Our Mission</h2>
        <p>At United Taxpert, our mission is to empower individuals and businesses with expert financial guidance, cutting-edge tax strategies, and unwavering commitment to accuracy. We combine 25+ years of trusted expertise with modern technology to deliver exceptional results. From tax analysis to cash flow management, we provide the clarity and strategy you need to make confident financial decisions and achieve lasting growth.</p>
        <a href="mailto:refunds@unitedtaxpert.com" className="btn btn-primary">Contact Us Now</a>
      </div>
    </div>
  </section>
);

const AccreditationsSection = () => (
  <section className="accreditations-section">
    <div className="container"><h2 className="acc-section-title">Accreditions</h2></div>
    <div className="acc-banner">
      <div className="container">
        <div className="acc-logos-grid">
          <div className="acc-logo-item refund-item">
            <div className="refund-content">
              <div className="refund-icon">&#9889;</div>
              <div className="refund-title">Get your refund fast here</div>
              <div className="refund-sub">Taxpayer Advances Available.</div>
            </div>
          </div>
          <div className="acc-logo-item irs-efile-item">
            <img src={IRS_EFILE_LOGO} alt="IRS Authorized e-file Provider" className="acc-logo-img" />
          </div>
          <div className="acc-logo-item afsp-text-item">
            <div className="afsp-content">
              <div className="afsp-star-row">&#9733; &#9733; &#9733; &#9733; &#9733;</div>
              <div className="afsp-title">IRS AFSP Record of Completion for 2026</div>
              <a href={AFSP_CERT_PDF} target="_blank" rel="noopener noreferrer" className="afsp-cert-link">&#128196; View IRS Certificate</a>
              <div className="afsp-divider" />
              <div className="afsp-verify-text">Verify my credentials on the official IRS Directory</div>
              <a href="https://irs.treasury.gov/rpo/rpo.jsf" target="_blank" rel="noopener noreferrer" className="afsp-irs-link">&#128279; IRS Preparer Directory</a>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div className="container">
      <h2 className="aff-section-title">Affiliations</h2>
      <div className="affiliations-grid">
        <div className="affiliation-card">
          <div className="natp-aff-logo"><div className="natp-aff-text">NATP</div><div className="natp-aff-name">National Association of Tax Professionals</div><div className="natp-aff-member">Member</div></div>
        </div>
        <div className="affiliation-card bbb-aff">
          <div className="bbb-aff-logo"><div className="bbb-shield"><FaShieldAlt size={36} /></div><div className="bbb-aff-text">BBB+</div><div className="bbb-aff-name">Better Business Bureau</div><div className="bbb-aff-rating">Accredited Business &#9733;&#9733;&#9733;&#9733;&#9733;</div></div>
        </div>
      </div>
    </div>
  </section>
);

const AboutTroubleSection = () => (
  <section className="trouble-section">
    <div className="trouble-overlay" />
    <div className="container trouble-content">
      <div className="trouble-text">
        <h2>Having Trouble Evaluating Your Finances?</h2>
        <p>Let United Taxpert make it simple. Our financial experts help you assess your current position, uncover inefficiencies, and create a clear path toward better financial health. From tax analysis to cash flow management, we provide the clarity and strategy you need to make confident financial decisions.</p>
        <a href="mailto:refunds@unitedtaxpert.com" className="btn btn-primary">Contact Us Now</a>
      </div>
      <div className="trouble-image">
        <img src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=500&auto=format&fit=crop&q=80" alt="Financial advisor" />
      </div>
    </div>
  </section>
);

const AboutPage = () => (
  <div className="site-wrapper">
    <Header />
    <AboutHero />
    <AboutStandOut />
    <MissionSection />
    <AccreditationsSection />
    <AboutTroubleSection />
    <Footer />
  </div>
);

const Home = () => (
  <div className="site-wrapper">
    <Header />
    <HeroSection />
    <ServiceHighlights />
    <StandOutSection />
    <QuoteSection />
    <ServicesSection />
    <TroubleSection />
    <PrioritySection />
    <Footer />
  </div>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/services/bookkeeping" element={<BookkeepingPage />} />
          <Route path="/services/tax-planning" element={<GenericServicePage serviceKey="tax-planning" />} />
          <Route path="/services/payroll-services" element={<GenericServicePage serviceKey="payroll-services" />} />
          <Route path="/services/inventory-management" element={<GenericServicePage serviceKey="inventory-management" />} />
          <Route path="/services/financial-reporting-audit" element={<GenericServicePage serviceKey="financial-reporting-audit" />} />
          <Route path="/services/business-acquisitions" element={<GenericServicePage serviceKey="business-acquisitions" />} />
          <Route path="/services/administrative-services" element={<GenericServicePage serviceKey="administrative-services" />} />
          <Route path="/services/fractional-cfo" element={<GenericServicePage serviceKey="fractional-cfo" />} />
          <Route path="/pricing" element={<PricingPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
